import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Plus,
  CheckCircle,
  Receipt,
  Users,
  Building2,
  Loader2,
  RefreshCw,
  AlertCircle,
  Wallet,
  PiggyBank,
  ArrowDownRight,
  ArrowUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const PTCDealFinancials = ({ clientId, clientName, dealAmount }) => {
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addEntryDialog, setAddEntryDialog] = useState({ open: false, type: null });
  const [entryAmount, setEntryAmount] = useState('');
  const [entryDescription, setEntryDescription] = useState('');
  const [entryReference, setEntryReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchFinancials = async () => {
    try {
      const response = await api.get(`/ptc-finance/deal/${clientId}`);
      setFinancials(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to fetch financials:', error);
      }
      setFinancials(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchFinancials();
    }
  }, [clientId]);

  const handleRecordPayment = async () => {
    if (!entryAmount || parseFloat(entryAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/ptc-finance/deal/${clientId}/record-payment`, null, {
        params: {
          amount: parseFloat(entryAmount),
          description: entryDescription || undefined,
          reference_id: entryReference || undefined
        }
      });
      toast.success('Payment recorded successfully');
      setAddEntryDialog({ open: false, type: null });
      setEntryAmount('');
      setEntryDescription('');
      setEntryReference('');
      fetchFinancials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordCost = async () => {
    if (!entryAmount || parseFloat(entryAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/ptc-finance/deal/${clientId}/record-partner-cost`, null, {
        params: {
          amount: parseFloat(entryAmount),
          description: entryDescription || 'Partner/Attorney cost',
          reference_id: entryReference || undefined
        }
      });
      toast.success('Partner cost recorded successfully');
      setAddEntryDialog({ open: false, type: null });
      setEntryAmount('');
      setEntryDescription('');
      setEntryReference('');
      fetchFinancials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record cost');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </CardContent>
      </Card>
    );
  }

  if (!financials) {
    return null;
  }

  return (
    <>
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="ptc-deal-financials">
        <CardHeader className="py-1.5 px-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xs flex items-center gap-1.5 text-emerald-800">
              <TrendingUp className="h-3.5 w-3.5" />
              Deal Profit Calculator
            </CardTitle>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={fetchFinancials}
                className="h-6 w-6 p-0"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
              {!financials.is_balanced && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                  <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                  Imbalanced
                </Badge>
              )}
              {financials.alerts && financials.alerts.length > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-600 border-amber-400">
                  <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                  {financials.alerts.length} Alert{financials.alerts.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3 pt-0">
          {/* Primary Financial Metrics — compact 4-col */}
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            <div className="p-1.5 bg-white/70 rounded border border-slate-200">
              <p className="text-[10px] text-slate-400 leading-tight">Deal Price</p>
              <p className="text-sm font-semibold text-slate-800">
                {formatCurrency(financials.deal_price || dealAmount)}
              </p>
            </div>
            <div className="p-1.5 bg-white/70 rounded border border-emerald-200">
              <p className="text-[10px] text-slate-400 leading-tight">Collected</p>
              <p className="text-sm font-semibold text-emerald-600">
                {formatCurrency(financials.total_collected)}
              </p>
            </div>
            <div className="p-1.5 bg-white/70 rounded border border-amber-200">
              <p className="text-[10px] text-slate-400 leading-tight">Remaining</p>
              <p className="text-sm font-semibold text-amber-600">
                {formatCurrency(financials.remaining_balance)}
              </p>
            </div>
            <div className="p-1.5 bg-white/70 rounded border border-blue-200">
              <p className="text-[10px] text-slate-400 leading-tight">Unallocated</p>
              <p className="text-sm font-semibold text-blue-600">
                {formatCurrency(financials.unallocated)}
              </p>
            </div>
          </div>

          {/* MONEY SPLIT — compact 3-col */}
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            <div className="p-1.5 bg-emerald-50 rounded border border-emerald-300">
              <p className="text-[10px] font-medium text-emerald-700">PTC</p>
              <p className="text-sm font-bold text-emerald-700">
                {formatCurrency(financials.ptc_amount)}
              </p>
            </div>
            <div className="p-1.5 bg-red-50 rounded border border-red-300">
              <p className="text-[10px] font-medium text-red-700">Partner</p>
              <p className="text-sm font-bold text-red-700">
                {formatCurrency(financials.partner_amount)}
              </p>
            </div>
            <div className="p-1.5 bg-purple-50 rounded border border-purple-300">
              <p className="text-[10px] font-medium text-purple-700">Commission</p>
              <p className="text-sm font-bold text-purple-700">
                {formatCurrency(financials.commission_amount)}
              </p>
            </div>
          </div>

          {/* Split Validation Banner */}
          {financials.split_balanced === false && (
            <div className="mb-2 p-1.5 bg-red-100 border border-red-300 rounded flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 text-red-600" />
              <p className="text-[10px] font-medium text-red-700">
                Split mismatch: ${Math.abs(financials.split_variance || 0).toFixed(2)}
              </p>
            </div>
          )}

          {/* Payout Eligibility */}
          {financials.total_collected > 0 && (
            <div className={`mb-2 p-2 rounded border ${
              financials.payout_eligible 
                ? 'bg-emerald-50 border-emerald-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-center gap-1.5">
                {financials.payout_eligible ? (
                  <>
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-700">Payout Eligible</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-xs font-medium text-red-700">Payout Blocked</span>
                  </>
                )}
              </div>
              {!financials.payout_eligible && financials.payout_block_reasons?.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {financials.payout_block_reasons.map((reason, i) => (
                    <li key={`block-${reason.slice(0,20)}-${i}`} className="text-[10px] text-red-600 flex items-start gap-1">
                      <span className="text-red-400">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Obligations — compact 3-col */}
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            <div className="p-1.5 bg-white/70 rounded border border-purple-200">
              <p className="text-[10px] text-slate-400 leading-tight">Comm. Owed</p>
              <p className="text-sm font-semibold text-purple-600">
                {formatCurrency(financials.commission_owed)}
              </p>
            </div>
            <div className="p-1.5 bg-white/70 rounded border border-red-200">
              <p className="text-[10px] text-slate-400 leading-tight">Partner Costs</p>
              <p className="text-sm font-semibold text-red-600">
                {formatCurrency(financials.partner_costs_owed)}
              </p>
            </div>
            <div className="p-1.5 bg-white/70 rounded border border-slate-200">
              <p className="text-[10px] text-slate-400 leading-tight">Refunds</p>
              <p className="text-sm font-semibold text-slate-600">
                {formatCurrency(financials.refunds)}
              </p>
            </div>
          </div>

          {/* Profit Section — compact 2-col */}
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <div className={`p-1.5 rounded border ${financials.net_profit_actual >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
              <p className="text-[10px] text-slate-500 font-medium">Net Profit (Actual)</p>
              <p className={`text-sm font-bold ${financials.net_profit_actual >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {formatCurrency(financials.net_profit_actual)}
              </p>
            </div>
            <div className="p-1.5 bg-slate-50 rounded border border-slate-200">
              <p className="text-[10px] text-slate-500 font-medium">Net Profit (Projected)</p>
              <p className="text-sm font-bold text-slate-700">
                {formatCurrency(financials.net_profit_projected)}
              </p>
            </div>
          </div>

          {/* Alerts — compact */}
          {financials.alerts && financials.alerts.length > 0 && (
            <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded">
              <p className="text-[10px] font-semibold text-amber-800 mb-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Deal Alerts
              </p>
              <ul className="space-y-0.5">
                {financials.alerts.map((alert, i) => (
                  <li key={`alert-${alert.slice(0,20)}-${i}`} className="text-[10px] text-amber-700 flex items-start gap-1">
                    <span className="text-amber-500">•</span>
                    {alert}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quick Actions — compact inline */}
          <div className="flex gap-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
              onClick={() => setAddEntryDialog({ open: true, type: 'payment' })}
            >
              <Plus className="h-3 w-3 mr-1" />
              Record Payment
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setAddEntryDialog({ open: true, type: 'cost' })}
            >
              <Building2 className="h-3 w-3 mr-1" />
              Record Partner Cost
            </Button>
          </div>

          {/* Ledger Entries — compact */}
          {financials.ledger_entries && financials.ledger_entries.length > 0 && (
            <div className="border-t border-emerald-200 pt-2 mt-2">
              <p className="text-[10px] font-medium text-emerald-700 mb-1">Recent Ledger</p>
              <div className="space-y-1 max-h-[100px] overflow-y-auto">
                {financials.ledger_entries.slice(0, 10).map((entry) => (
                  <div
                    key={entry.ledger_id}
                    className="flex items-center justify-between text-[10px] p-1.5 bg-white/50 rounded border"
                  >
                    <div className="flex items-center gap-1.5">
                      {entry.entry_type === 'client_payment' && <Receipt className="h-3 w-3 text-emerald-600" />}
                      {entry.entry_type === 'partner_cost' && <Building2 className="h-3 w-3 text-red-600" />}
                      {entry.entry_type === 'commission_payout' && <Users className="h-3 w-3 text-purple-600" />}
                      {entry.entry_type === 'refund' && <ArrowUpRight className="h-3 w-3 text-amber-600" />}
                      <span className="text-slate-600">{entry.description || entry.entry_type}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`font-medium ${
                        entry.entry_type === 'client_payment' ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {entry.entry_type === 'client_payment' ? '+' : '-'}{formatCurrency(entry.amount)}
                      </span>
                      <Badge variant={entry.status === 'completed' ? 'secondary' : 'outline'} className="text-[9px] px-1 py-0">
                        {entry.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={addEntryDialog.open} onOpenChange={(open) => setAddEntryDialog({ open, type: open ? addEntryDialog.type : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {addEntryDialog.type === 'payment' ? 'Record Client Payment' : 'Record Partner Cost'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={entryAmount}
                  onChange={(e) => setEntryAmount(e.target.value)}
                  className="pl-10"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={entryDescription}
                onChange={(e) => setEntryDescription(e.target.value)}
                className="mt-1"
                placeholder={addEntryDialog.type === 'payment' ? 'Client payment received' : 'Partner/Attorney cost'}
              />
            </div>
            <div>
              <Label>Reference ID (optional)</Label>
              <Input
                value={entryReference}
                onChange={(e) => setEntryReference(e.target.value)}
                className="mt-1"
                placeholder="Invoice #, Check #, etc."
              />
            </div>
            {addEntryDialog.type === 'payment' && (
              <div className="p-3 bg-purple-50 rounded-lg text-xs text-purple-700">
                <p className="font-medium">Commission will be auto-calculated</p>
                <p>15% commission will be created as pending payout.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEntryDialog({ open: false, type: null })}>
              Cancel
            </Button>
            <Button
              onClick={addEntryDialog.type === 'payment' ? handleRecordPayment : handleRecordCost}
              disabled={submitting}
              className={addEntryDialog.type === 'payment' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {addEntryDialog.type === 'payment' ? 'Record Payment' : 'Record Cost'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PTCDealFinancials;
